import { useCallback, useEffect, useMemo, useState } from 'react'
import { ShieldCheck, Pencil, X, Trash2, Info } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Tooltip,
  useToast,
} from '@/components/ui'

// ── Types ────────────────────────────────────────────────────────────────────

interface PolicyOverride {
  value: string
  reason: string
  approvedAt: string | null
  approvedByUserId: string | null
}

interface PolicyItem {
  id: string
  policyKey: string
  category: string
  sortOrder: number
  name: string
  descriptionAdmin: string
  descriptionUser: string
  platform: 'ad' | 'entra' | 'both' | 'external'
  adImplementation: string | null
  entraImplementation: string | null
  dataType: 'boolean' | 'integer' | 'enum' | 'duration'
  defaultValue: string
  allowedValues: unknown
  scoreWeight: 'critical' | 'high' | 'medium' | 'low'
  scoreCondition: string | null
  override: PolicyOverride | null
  effectiveValue: string
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const SCORE_WEIGHT_LABEL: Record<string, string> = {
  critical: 'Kritisch',
  high: 'Hoch',
  medium: 'Mittel',
  low: 'Niedrig',
}

const SCORE_WEIGHT_CLASS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-gray-100 text-gray-600',
}

const PLATFORM_LABEL: Record<string, string> = {
  ad: 'Active Directory',
  entra: 'Entra ID',
  both: 'AD + Entra',
  external: 'Extern',
}

function extractError(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string }
  return e?.response?.data?.error ?? e?.message ?? 'Unbekannter Fehler'
}

// ── Edit-Default-Value Modal ──────────────────────────────────────────────────

interface EditDefaultModalProps {
  policy: PolicyItem
  onSave: (value: string) => Promise<void>
  onClose: () => void
}

function EditDefaultModal({ policy, onSave, onClose }: EditDefaultModalProps) {
  const [value, setValue] = useState(policy.defaultValue)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit() {
    setSaving(true)
    setError('')
    try {
      await onSave(value)
      onClose()
    } catch (err) {
      setError(extractError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={`Standard bearbeiten: ${policy.name}`} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-gray-500">{policy.descriptionAdmin}</p>
        <div>
          <label className="block text-sm font-medium mb-1">implec-Standardwert</label>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={policy.dataType === 'boolean' ? 'true / false' : 'Wert eingeben'}
          />
          {policy.dataType === 'boolean' && (
            <p className="text-xs text-gray-400 mt-1">Erlaubte Werte: true, false</p>
          )}
          {!!policy.allowedValues && typeof policy.allowedValues === 'object' && !Array.isArray(policy.allowedValues) && (
            <p className="text-xs text-gray-400 mt-1">
              Bereich: {(policy.allowedValues as { min: number; max: number }).min}–{(policy.allowedValues as { min: number; max: number }).max}
            </p>
          )}
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSubmit} loading={saving}>Speichern</Button>
        </div>
      </div>
    </Modal>
  )
}

// ── Customer Override Modal ───────────────────────────────────────────────────

interface OverrideModalProps {
  policy: PolicyItem
  customerId: number
  onSave: (value: string, reason: string) => Promise<void>
  onClose: () => void
}

function OverrideModal({ policy, customerId: _customerId, onSave, onClose }: OverrideModalProps) {
  const [value, setValue] = useState(policy.override?.value ?? policy.defaultValue)
  const [reason, setReason] = useState(policy.override?.reason ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit() {
    if (!reason.trim()) {
      setError('Begründung ist erforderlich.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSave(value, reason)
      onClose()
    } catch (err) {
      setError(extractError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={`Kunden-Override: ${policy.name}`} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-gray-500">{policy.descriptionAdmin}</p>
        <div>
          <label className="block text-sm font-medium mb-1">
            Standard: <span className="font-mono">{policy.defaultValue}</span>
          </label>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Kunden-Wert</label>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={policy.dataType === 'boolean' ? 'true / false' : 'Abweichender Wert'}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Begründung *</label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Warum weicht dieser Kunde vom Standard ab?"
            rows={3}
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSubmit} loading={saving}>Override speichern</Button>
        </div>
      </div>
    </Modal>
  )
}

// ── Policy Row ────────────────────────────────────────────────────────────────

interface PolicyRowProps {
  item: PolicyItem
  onEditDefault: (item: PolicyItem) => void
  onAddOverride: (item: PolicyItem) => void
  onDeleteOverride: (item: PolicyItem) => void
  showOverrideActions: boolean
}

function PolicyRow({ item, onEditDefault, onAddOverride, onDeleteOverride, showOverrideActions }: PolicyRowProps) {
  const hasOverride = !!item.override

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="flex items-start gap-2">
          <div>
            <div className="font-medium text-sm">{item.name}</div>
            <div className="text-xs text-gray-400 font-mono">{item.policyKey}</div>
          </div>
          <Tooltip content={item.descriptionAdmin}>
            <Info className="w-3.5 h-3.5 text-gray-300 mt-0.5 flex-shrink-0 cursor-help" />
          </Tooltip>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${SCORE_WEIGHT_CLASS[item.scoreWeight] ?? ''}`}>
          {SCORE_WEIGHT_LABEL[item.scoreWeight]}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-gray-500">{PLATFORM_LABEL[item.platform]}</td>
      <td className="px-4 py-3">
        <span className="font-mono text-sm">{item.defaultValue}</span>
        <IconButton
          icon={<Pencil />}
          aria-label="Standard bearbeiten"
          size="xs"
          className="ml-2 opacity-0 group-hover:opacity-100"
          onClick={() => onEditDefault(item)}
        />
      </td>
      {showOverrideActions && (
        <>
          <td className="px-4 py-3">
            {hasOverride ? (
              <div>
                <span className="font-mono text-sm text-amber-700">{item.override!.value}</span>
                <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]" title={item.override!.reason}>
                  {item.override!.reason}
                </div>
              </div>
            ) : (
              <span className="text-gray-300 text-xs">—</span>
            )}
          </td>
          <td className="px-4 py-3">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => onAddOverride(item)}
              >
                {hasOverride ? 'Bearbeiten' : 'Override'}
              </Button>
              {hasOverride && (
                <IconButton
                  icon={<Trash2 />}
                  aria-label="Override löschen"
                  size="xs"
                  className="text-red-400"
                  onClick={() => onDeleteOverride(item)}
                />
              )}
            </div>
          </td>
        </>
      )}
      {!showOverrideActions && (
        <td className="px-4 py-3">
          <IconButton
            icon={<Pencil />}
            aria-label="Standard bearbeiten"
            size="xs"
            onClick={() => onEditDefault(item)}
          />
        </td>
      )}
    </tr>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function SecurityPoliciesPage() {
  const { toast } = useToast()
  const [policies, setPolicies] = useState<PolicyItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editingDefault, setEditingDefault] = useState<PolicyItem | null>(null)
  const [overrideTarget, setOverrideTarget] = useState<PolicyItem | null>(null)
  const [pendingDeleteOverride, setPendingDeleteOverride] = useState<PolicyItem | null>(null)
  const [customerIdInput, setCustomerIdInput] = useState('')
  const [customerId, setCustomerId] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = customerId ? `?customerId=${customerId}` : ''
      const { data } = await apiClient.get<PolicyItem[]>(`/security-policies${params}`)
      setPolicies(data)
    } catch (err) {
      setError(extractError(err))
    } finally {
      setLoading(false)
    }
  }, [customerId])

  useEffect(() => { load() }, [load])

  const categories = useMemo(() => {
    const map = new Map<string, PolicyItem[]>()
    for (const p of policies) {
      if (!map.has(p.category)) map.set(p.category, [])
      map.get(p.category)!.push(p)
    }
    return map
  }, [policies])

  async function handleSaveDefault(value: string) {
    if (!editingDefault) return
    await apiClient.patch(`/security-policies/${editingDefault.policyKey}`, { defaultValue: value })
    toast({ title: 'Standard gespeichert', tone: 'success' })
    await load()
  }

  async function handleSaveOverride(value: string, reason: string) {
    if (!overrideTarget || !customerId) return
    await apiClient.post(`/security-policies/overrides/${customerId}/${overrideTarget.policyKey}`, { value, reason })
    toast({ title: 'Override gespeichert', tone: 'success' })
    await load()
  }

  async function handleDeleteOverride() {
    if (!pendingDeleteOverride || !customerId) return
    await apiClient.delete(`/security-policies/overrides/${customerId}/${pendingDeleteOverride.policyKey}`)
    setPendingDeleteOverride(null)
    toast({ title: 'Override entfernt', tone: 'success' })
    await load()
  }

  function handleLoadCustomer() {
    const id = parseInt(customerIdInput)
    if (isNaN(id) || id <= 0) {
      toast({ title: 'Ungültige Kunden-ID', tone: 'error' })
      return
    }
    setCustomerId(id)
  }

  const showOverrideActions = !!customerId

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} onRetry={load} />

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sicherheitsrichtlinien"
        icon={<ShieldCheck />}
        breadcrumbs={[
          { label: 'Infrastruktur' },
          { label: 'Sicherheitsrichtlinien' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Input
              value={customerIdInput}
              onChange={(e) => setCustomerIdInput(e.target.value)}
              placeholder="Kunden-ID für Overrides"
              className="w-48"
            />
            <Button
              variant={customerId ? 'secondary' : 'primary'}
              size="sm"
              onClick={handleLoadCustomer}
            >
              Kunden laden
            </Button>
            {customerId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setCustomerId(null); setCustomerIdInput('') }}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        }
      />

      {customerId && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800">
          Kunden-Modus aktiv (ID: {customerId}). Gelb markierte Werte sind Kunden-Abweichungen vom Standard.
        </div>
      )}

      <Tabs defaultValue={Array.from(categories.keys())[0] ?? ''}>
        <TabsList className="flex-wrap">
          {Array.from(categories.keys()).map((cat) => (
            <TabsTrigger key={cat} value={cat}>
              {cat}
              <span className="ml-1.5 text-xs text-gray-400">({categories.get(cat)!.length})</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {Array.from(categories.entries()).map(([cat, items]) => (
          <TabsContent key={cat} value={cat}>
            <Card>
              <Card.Body className="p-0">
                {items.length === 0 ? (
                  <EmptyState message="Keine Richtlinien in dieser Kategorie" />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="px-4 py-2 text-left font-medium text-gray-600">Parameter</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-600">Score-Gewicht</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-600">Plattform</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-600">implec-Standard</th>
                          {showOverrideActions && (
                            <>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Kunden-Override</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Aktionen</th>
                            </>
                          )}
                          {!showOverrideActions && (
                            <th className="px-4 py-2 text-left font-medium text-gray-600">Aktionen</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="group">
                        {items.map((item) => (
                          <PolicyRow
                            key={item.policyKey}
                            item={item}
                            onEditDefault={setEditingDefault}
                            onAddOverride={setOverrideTarget}
                            onDeleteOverride={setPendingDeleteOverride}
                            showOverrideActions={showOverrideActions}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {editingDefault && (
        <EditDefaultModal
          policy={editingDefault}
          onSave={handleSaveDefault}
          onClose={() => setEditingDefault(null)}
        />
      )}

      {overrideTarget && customerId && (
        <OverrideModal
          policy={overrideTarget}
          customerId={customerId}
          onSave={handleSaveOverride}
          onClose={() => setOverrideTarget(null)}
        />
      )}

      {pendingDeleteOverride && (
        <ConfirmDialog
          isOpen
          title="Override löschen"
          message={`Override für „${pendingDeleteOverride.name}" wird entfernt. Der Kunde fällt auf den implec-Standard zurück.`}
          confirmLabel="Löschen"
          variant="danger"
          onConfirm={handleDeleteOverride}
          onCancel={() => setPendingDeleteOverride(null)}
        />
      )}
    </div>
  )
}
